/**
 * src/components/BackToHome.jsx
 *
 * Floating "← Volver al inicio" link.
 *
 * ¿Por qué existe?
 * Cliente reportó que al entrar a /login, /planes, /signup, /forgot-password,
 * /reset-password — quedaba "atrapado": la página solo tenía el form, sin
 * forma de regresar al landing. Antes tenía que tipear la URL o usar el
 * botón back del browser (que muchos no usan).
 *
 * Comportamiento:
 *   - Se renderiza desde (public)/layout.js (1 sola fuente de verdad)
 *   - Se OCULTA en la home (`/`) — el header de la landing ya tiene
 *     navegación; sería redundante
 *   - Se OCULTA en /legal/* — esas páginas tienen su propio layout
 *     con su propia navegación
 *   - En el resto de páginas públicas se ve fijo arriba a la izquierda
 *     con un fondo glassmorphism gold sutil
 *
 * Accesibilidad: link con aria-label explícito, focus-visible con
 * outline gold, contraste WCAG AA pasado.
 */

'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ArrowLeft } from '@phosphor-icons/react/dist/ssr';

// Paths donde NO debe aparecer (la home y las que tienen su propia nav).
const HIDDEN_ON = [
  '/',                  // landing — ya tiene header con nav completo
  '/funciones',         // tiene el header de la landing (logo → inicio) y menú propio
  '/planes',            // ídem: header propio con logo → inicio y menú ☰
  '/planesbeta',
];

const HIDDEN_PREFIXES = [
  '/legal',             // legal pages tienen layout propio con back link
];

export default function BackToHome() {
  const pathname = usePathname() || '/';

  if (HIDDEN_ON.includes(pathname)) return null;
  if (HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))) return null;

  return (
    <Link
      href="/"
      aria-label="Volver al inicio"
      // Desde la app (?app=1, ver ModoApp) no se muestra: lleva al sitio con precios (3.1.1).
      className="ocultarEnApp"
      style={{
        position: 'fixed',
        top: 18,
        left: 18,
        zIndex: 100,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 14px 8px 12px',
        background: 'rgba(15, 15, 16, 0.78)',
        border: '1px solid rgba(197, 160, 89, 0.35)',
        borderRadius: 999,
        color: '#D4B47A',
        fontFamily: 'var(--font-manrope), -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        fontSize: '0.78rem',
        fontWeight: 600,
        letterSpacing: '0.01em',
        textDecoration: 'none',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
        transition: 'transform 0.18s ease, background 0.18s ease, color 0.18s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(15, 15, 16, 0.92)';
        e.currentTarget.style.color = '#E8C988';
        e.currentTarget.style.transform = 'translateX(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(15, 15, 16, 0.78)';
        e.currentTarget.style.color = '#D4B47A';
        e.currentTarget.style.transform = 'translateX(0)';
      }}
    >
      <ArrowLeft size={14} weight="bold" aria-hidden="true" />
      <span>Volver al inicio</span>
    </Link>
  );
}
